import React, { useState, useEffect, useRef } from 'react';
import { GOOGLE_MAPS_CONFIG } from '../config/googleMaps';
import ManualAddressInput from './ManualAddressInput';
import LocationHelper from './LocationHelper';

/**
 * Reusable Address Picker Map Component
 * Modern, mobile-responsive design matching Zepto style
 */
const AddressPickerMap = ({
  onAddressSelect,
  height = '400px',
  defaultLocation = { lat: 29.010975440471878, lng: 78.29746225012005 },
  className = '',
  showSearchBar = true,
  showCurrentLocationBtn = true
}) => {
  const mapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [showLocationHelper, setShowLocationHelper] = useState(false);
  const [mapLoadFailed, setMapLoadFailed] = useState(false);
  
  // Google Maps objects
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    initializeMap();
  }, []);

  const loadGoogleMapsAPI = () => {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        resolve(window.google);
        return;
      }

      // Check if API key is available
      if (!GOOGLE_MAPS_CONFIG.apiKey || GOOGLE_MAPS_CONFIG.apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {

        reject(new Error('Google Maps API key not configured'));
        return;
      }



      // Create script tag
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_CONFIG.apiKey}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      // Set up callback
      window.initGoogleMaps = () => {
        resolve(window.google);
        delete window.initGoogleMaps;
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);

      // Timeout after 10 seconds
      setTimeout(() => {
        if (!window.google || !window.google.maps) {
          reject(new Error('Google Maps API load timeout'));
        }
      }, 10000);
    });
  };

  const initializeMap = async () => {
    try {
      setLoading(true);
      setError(null);
      setMapLoadFailed(false);

      const mapContainer = mapRef.current;
      if (!mapContainer) return;



      // Check if API key is available
      if (!GOOGLE_MAPS_CONFIG.apiKey || GOOGLE_MAPS_CONFIG.apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
        const errorMessage = `Google Maps API key not configured. Environment: ${process.env.NODE_ENV}. Please check your environment variables.`;
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      // Load Google Maps API
      await loadGoogleMapsAPI();

      // Initialize map
      const mapInstance = new window.google.maps.Map(mapContainer, {
        center: defaultLocation,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        mapTypeId: 'roadmap'
      });

      setMap(mapInstance);

      // Add click listener
      mapInstance.addListener('click', handleMapClick);

      // Add initial marker
      const initialMarker = new window.google.maps.Marker({
        position: defaultLocation,
        map: mapInstance,
        title: 'Selected Location',
        draggable: true
      });

      setMarker(initialMarker);

      // Handle marker drag
      initialMarker.addListener('dragend', (event) => {
        const position = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        handleLocationSelect(position);
      });

      setLoading(false);
    } catch (error) {
      console.error('Map initialization failed:', error);
      let errorMessage = 'Failed to load map. ';
      
      if (error.message.includes('API key')) {
        errorMessage += 'Map service is temporarily unavailable. Please use manual address entry.';
      } else if (error.message.includes('timeout')) {
        errorMessage += 'Map loading timed out. Please check your internet connection and try again.';
      } else {
        errorMessage += 'Please refresh the page and try again, or use manual address entry.';
      }
      
      setError(errorMessage);
      setMapLoadFailed(true);
      setLoading(false);
    }
  };

  const handleMapClick = async (event) => {
    const position = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    console.log('[AddressPickerMap] Map clicked at:', position);
    await handleLocationSelect(position);
  };

  const handleLocationSelect = async (position) => {
    try {
      setLoading(true);
      console.log('[AddressPickerMap] Selecting location:', position);

      // Update marker position
      if (marker) {
        marker.setPosition(position);
      }

      // Get address details using Geocoding API
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: position }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error('Geocoding failed: ' + status));
          }
        });
      });

      // Parse address components
      const addressComponents = result.address_components;
      const addressData = {
        coordinates: position,
        formatted: result.formatted_address || 'Unknown location',
        street: '',
        area: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      };

      // Extract address components
      addressComponents.forEach(component => {
        const types = component.types;
        if (types.includes('street_number') || types.includes('route')) {
          addressData.street += component.long_name + ' ';
        } else if (types.includes('sublocality') || types.includes('neighborhood')) {
          addressData.area = component.long_name;
        } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          addressData.city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          addressData.state = component.long_name;
        } else if (types.includes('postal_code')) {
          addressData.pincode = component.long_name;
        } else if (types.includes('country')) {
          addressData.country = component.long_name;
        }
      });

      addressData.street = addressData.street.trim();

      setSelectedAddress(addressData);
      console.log('[AddressPickerMap] Address resolved:', addressData);
      
      // Notify parent component
      if (onAddressSelect) {
        console.log('[AddressPickerMap] Calling onAddressSelect callback');
        onAddressSelect(addressData);
      } else {
        console.warn('[AddressPickerMap] No onAddressSelect callback provided');
      }

      setLoading(false);
    } catch (error) {
      console.error('Address selection failed:', error);
      setError('Failed to get address details. Please try again.');
      setLoading(false);
    }
  };

  const detectCurrentLocation = async () => {
    if (isDetectingLocation) return;

    setIsDetectingLocation(true);
    setError(null);

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser. Please select your location manually on the map.');
      setIsDetectingLocation(false);
      return;
    }

    try {
      // Check location permission
      const permission = await checkLocationPermission();
      if (permission === 'denied') {
        setShowLocationHelper(true);
        setError('Location access was denied. Please check the help below to enable location permissions.');
        setIsDetectingLocation(false);
        return;
      }
      // If permission is 'prompt' or 'granted', proceed with the geolocation request

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            console.log('[GPS] Current location detected:', {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              altitudeAccuracy: pos.coords.altitudeAccuracy,
              heading: pos.coords.heading,
              speed: pos.coords.speed
            });
            resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            });
          },
          (error) => {
            console.error('[GPS] Location detection error:', error);
            let errorMessage = 'Could not detect your location. ';
            
            switch(error.code) {
              case error.PERMISSION_DENIED:
                setShowLocationHelper(true);
                errorMessage += 'Location access was denied. Please check the guide below to enable location permissions.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage += 'Location information is unavailable. This might happen if you\'re indoors or GPS is not working. Please select your location manually on the map.';
                break;
              case error.TIMEOUT:
                errorMessage += 'Location request timed out. Please make sure GPS is enabled and try again, or select your location manually on the map.';
                break;
              default:
                errorMessage += 'An unknown error occurred. Please select your location manually on the map.';
                break;
            }
            
            reject(new Error(errorMessage));
          },
          {
            enableHighAccuracy: true, // Use GPS for accurate location
            timeout: 30000, // 30 seconds timeout for GPS to acquire signal
            maximumAge: 0 // Don't use cached location - get fresh position
          }
        );
      });

      console.log('[GPS] Final position to use:', position);
      console.log('[GPS] Accuracy: ±' + (position.accuracy ? Math.round(position.accuracy) + ' meters' : 'unknown'));
      
      // Pan map to current location with better zoom
      if (map && marker) {
        console.log('[GPS] Moving map and marker to detected position');
        
        // Animate to new position
        map.panTo({ lat: position.lat, lng: position.lng });
        map.setZoom(18); // Closer zoom for current location
        
        // Update marker
        marker.setPosition({ lat: position.lat, lng: position.lng });
      }
      
      // Select this location to get address details
      await handleLocationSelect(position);

      // Show accuracy warning if needed
      if (position.accuracy && position.accuracy > 100) {
        console.warn('[GPS] Location accuracy is low:', position.accuracy, 'meters');
        setError(`Location detected with ±${Math.round(position.accuracy)}m accuracy. For better accuracy, ensure GPS is enabled and you're outdoors with clear sky view.`);
      } else if (position.accuracy && position.accuracy > 50) {
        console.log('[GPS] Location accuracy is moderate:', position.accuracy, 'meters');
      } else {
        console.log('[GPS] Location detected with good accuracy');
      }

    } catch (error) {
      console.error('Location detection failed:', error);
      setError(error.message || 'Could not detect your location. Please select manually on the map.');
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !map) return;

    try {
      setLoading(true);
      
      const service = new window.google.maps.places.PlacesService(map);
      const request = {
        query: searchQuery,
        fields: ['name', 'geometry', 'formatted_address']
      };

      service.textSearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results[0]) {
          const place = results[0];
          const position = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          
          map.setCenter(position);
          map.setZoom(16);
          handleLocationSelect(position);
          setShowSearch(false);
          setSearchQuery('');
        } else {
          setError('Location not found. Please try a different search term.');
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Search failed:', error);
      setError('Search failed. Please try again.');
      setLoading(false);
    }
  };

  // Helper function to check location permissions
  const checkLocationPermission = async () => {
    if (!navigator.permissions) {
      return 'unknown';
    }
    
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted', 'denied', or 'prompt'
    } catch (error) {
      return 'unknown';
    }
  };

  // If showing manual input, render that instead
  if (showManualInput) {
    return (
      <ManualAddressInput
        onAddressSelect={(address) => {
          setSelectedAddress(address);
          if (onAddressSelect) {
            onAddressSelect(address);
          }
          setShowManualInput(false);
        }}
        onCancel={() => setShowManualInput(false)}
      />
    );
  }

  return (
    <div className={`address-picker-map ${className}`}>
      {/* Show Location Helper for permission issues */}
      {showLocationHelper && (
        <div className="mb-3">
          <LocationHelper 
            onManualEntry={() => {
              setShowLocationHelper(false);
              setShowManualInput(true);
            }}
          />
        </div>
      )}

      {/* Error Message with Manual Input Option */}
      {(error || mapLoadFailed) && !showLocationHelper && (
        <div className="alert alert-warning" role="alert">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <strong>Map not available</strong>
              <p className="mb-0 small mt-1">
                {mapLoadFailed 
                  ? "Could not load map. Please enter your address manually or try refreshing the page."
                  : error
                }
              </p>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowManualInput(true)}
              style={{backgroundColor: '#2A632D', borderColor: '#2A632D'}}
            >
              Enter Manually
            </button>
          </div>
        </div>
      )}
      {/* Search Bar */}
      {showSearchBar && (
        <div className="search-container mb-3">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search for area, street name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                borderRadius: '8px 0 0 8px',
                border: '2px solid #2A632D',
                fontSize: '14px'
              }}
            />
            <button
              className="btn"
              type="button"
              onClick={handleSearch}
              disabled={loading || !searchQuery.trim()}
              style={{
                backgroundColor: '#2A632D',
                borderColor: '#2A632D',
                color: 'white',
                borderRadius: '0 8px 8px 0'
              }}
            >
              🔍
            </button>
          </div>
          <div className="mt-2 text-center">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowManualInput(true)}
              style={{borderColor: '#2A632D', color: '#2A632D'}}
            >
              Or enter address manually
            </button>
          </div>
        </div>
      )}

      {/* Map Container */}
      <div className="map-wrapper position-relative" style={{ height }}>
        <div
          ref={mapRef}
          className="w-100 h-100"
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}
        />

        {/* Loading Overlay */}
        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ borderRadius: '12px' }}>
            <div className="text-center">
              <div className="spinner-border text-success mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mb-0 text-muted">Loading map...</p>
            </div>
          </div>
        )}

        {/* Current Location Button */}
        {showCurrentLocationBtn && (
          <button
            className="btn btn-white shadow-sm position-absolute"
            style={{
              top: '10px',
              right: '10px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              border: '1px solid #ddd',
              zIndex: 1000
            }}
            onClick={detectCurrentLocation}
            disabled={isDetectingLocation}
            title="Detect current location"
          >
            {isDetectingLocation ? (
              <div className="spinner-border spinner-border-sm text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              '📍'
            )}
          </button>
        )}

        {/* Center Pin Indicator */}

      </div>

      {/* Error Message */}
      {!mapLoadFailed && error && (
        <div className="alert alert-warning mt-3 mb-0" role="alert">
          <div className="d-flex justify-content-between align-items-center">
            <small>{error}</small>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowManualInput(true)}
              style={{borderColor: '#2A632D', color: '#2A632D'}}
            >
              Enter Manually
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .address-picker-map {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .search-container input:focus {
          border-color: #2A632D;
          box-shadow: 0 0 0 0.2rem rgba(42, 99, 45, 0.25);
        }
        
        .btn:focus {
          box-shadow: 0 0 0 0.2rem rgba(42, 99, 45, 0.25);
        }
        
        .card.border-success {
          border-width: 2px;
        }
        
        @media (max-width: 768px) {
          .map-wrapper {
            height: 300px !important;
          }
          
          .search-container input {
            font-size: 16px; /* Prevents zoom on iOS */
          }
        }
      `}</style>
    </div>
  );
};

export default AddressPickerMap;
